import { Button, CircularProgress } from "@mui/material";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../../shared/axios/axiosProxy.js";
import i18n from "../../../utils/i18n.js";
import { createCalComUrl } from "../../utils/companyUtils.js";
import "./payment.scss";

const StripeCTA = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const email = query.get("email");
  const priceId = query.get("priceId");
  const reqId = query.get("reqId");
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [fullCalComURL, setFullCalComURL] = useState(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        // 1) Fetch the correct publishable key based on reqId
        const paramsResponse = await axios.get(
          `/api/aspreq/get-params/${reqId}`,
        );
        const { stripePublishableKey } = paramsResponse.data;

        if (!stripePublishableKey) {
          console.error("No Stripe publishable key returned from backend");
          return;
        }

        // 2) Initialize Stripe with the correct key
        const stripe = loadStripe(stripePublishableKey);
        setStripePromise(stripe);

        // 3) Create checkout session
        const response = await axios.post(
          `/api/checkout-cta/session?email=${encodeURIComponent(
            email,
          )}&priceId=${encodeURIComponent(priceId)}&reqId=${encodeURIComponent(
            reqId,
          )}`,
        );
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error(
          "Stripe initialization error:",
          error.response?.data || error.message,
        );
      }
    };

    if (reqId) {
      initializeStripe();
    }
  }, [email, priceId, reqId]);

  const options = {
    clientSecret,
    onComplete: () => {
      setCheckoutComplete(true);
    },
  };

  useEffect(() => {
    const buildCalComUrl = async () => {
      try {
        const response = await axios.get(`/api/aspreq/${reqId}`);
        const aspRequest = response.data || {};
        const nextUrl = createCalComUrl({
          reqId,
          compCtcName: aspRequest.companyctcname || "",
          compEmail: aspRequest.email || email || "",
          compPhone: aspRequest.companyPhone || "",
        });
        setFullCalComURL(nextUrl || null);
      } catch (error) {
        console.error(
          "Failed to build Cal.com URL:",
          error.response?.data || error.message,
        );
      }
    };

    if (checkoutComplete && reqId) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      buildCalComUrl();
    }
  }, [checkoutComplete, reqId, email]);

  return (
    <div className={"payment-container stripe-cta-container"}>
      <div className={"payment-content-area"}>
        <div className="payment-content-header">
          <p className="payment-header-text">
            {i18n.t("searchflow.providePaymentDetails")}
          </p>
        </div>
        <div id="checkout">
          {clientSecret && stripePromise ? (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div
              style={{
                minHeight: "240px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "14px",
              }}
            >
              <CircularProgress size={44} thickness={3.6} />
              <div
                style={{ color: "#3a4256", fontSize: "1rem", fontWeight: 500 }}
              >
                {i18n.t("general.stripepaymentloading")}
              </div>
            </div>
          )}
        </div>
        {checkoutComplete && (
          <div
            style={{
              marginTop: "16px",
              padding: "16px 48px",
              borderRadius: "10px",
              // background: "#f7f8ff",
              color: "#2c2f3a",
              fontSize: "0.95rem",
              lineHeight: 1.5,
              textAlign: "left",
            }}
          >
            <p
              style={{
                margin: "0 0 10px",
                fontSize: "16px",
                fontWeight: "300",
              }}
            >
              {i18n.t("oneClick.thankYou")}
            </p>
            <p
              style={{
                margin: "0 0 10px",
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {i18n.t("oneClick.safeHands")}
            </p>
            <p
              style={{
                margin: "0 0 10px",
                fontSize: "16px",
                fontWeight: "300",
              }}
            >
              {i18n.t("oneClick.bookingIntro")}
            </p>
            {fullCalComURL && (
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "48px",
                  marginTop: "32px",
                }}
              >
                <Button
                  component="a"
                  href={fullCalComURL}
                  fullWidth
                  style={{
                    // maxWidth: "428px",
                    width: "100%",
                    height: "44px",
                  }}
                  sx={{
                    bgcolor: "#fdb400",
                    color: "#141414",
                    fontWeight: 700,
                    letterSpacing: "normal",
                    py: 1.3,
                    borderRadius: 2,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#e59a00" },
                  }}
                >
                  {i18n.t("general.bookingappointment")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeCTA;
